'use client';

import { useCallback, useEffect, useReducer, useRef } from 'react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useSaveBillingAddress } from '@/hooks/queries/useBillingAddress';
import { lookupCep } from '@/lib/cep';
import { UF_OPTIONS } from '@/lib/brazil';
import type { BillingAddress } from '@/types/billing';
import {
  billingAddressReducer,
  initialBillingAddressDraft,
  normalizeCep,
  toSetBillingAddressRequest,
  validateBillingAddress,
  type BillingAddressField,
} from './billingAddress.helpers';

export function BillingAddressForm({
  address,
  autoFocusCep = false,
  onSaved,
  onCancel,
}: {
  address: BillingAddress | null;
  /** The Assinar pre-flight lands the user here — start at the first field. */
  autoFocusCep?: boolean;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const [draft, dispatch] = useReducer(billingAddressReducer, address, initialBillingAddressDraft);
  const { save, saving } = useSaveBillingAddress();
  const cepInputRef = useRef<HTMLInputElement>(null);
  const lookupAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (autoFocusCep) cepInputRef.current?.focus();
  }, [autoFocusCep]);

  useEffect(() => () => lookupAbortRef.current?.abort(), []);

  const handleCepChanged = useCallback((_field: BillingAddressField, value: string) => {
    dispatch({ type: 'field_changed', field: 'cep', value });
    const digits = normalizeCep(value);
    // ANY edit dooms the in-flight lookup (its CEP is no longer the field's),
    // so abort before the length check — not only when a new lookup starts.
    lookupAbortRef.current?.abort();
    // Fires on the 8th digit, not on blur — on mobile the keyboard covers the
    // fields below, so a blur-triggered lookup feels dead.
    if (digits.length !== 8) return;
    const controller = new AbortController();
    lookupAbortRef.current = controller;
    dispatch({ type: 'lookup_started', cep: digits });
    void lookupCep(digits, controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      dispatch({ type: 'lookup_settled', cep: digits, result });
    });
  }, []);

  const handleFieldChanged = useCallback((field: BillingAddressField, value: string) => {
    dispatch({ type: 'field_changed', field, value });
  }, []);

  const handleUfChanged = useCallback((value: string) => {
    dispatch({ type: 'field_changed', field: 'uf', value });
  }, []);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const errors = validateBillingAddress(draft.values);
      if (Object.values(errors).some(Boolean)) {
        dispatch({ type: 'invalid', errors });
        return;
      }
      dispatch({ type: 'saving', value: true });
      save(toSetBillingAddressRequest(draft.values))
        .then(() => {
          toast.success('Endereço de cobrança salvo.');
          onSaved();
        })
        .catch(() => {
          toast.error('Não foi possível salvar o endereço de cobrança.');
        })
        .finally(() => dispatch({ type: 'saving', value: false }));
    },
    [draft.values, onSaved, save],
  );

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      {draft.lookupNotice && (
        <Alert variant="neutral" role="status">{draft.lookupNotice}</Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <AddressField
          label="CEP" field="cep" value={draft.values.cep} error={draft.errors.cep}
          inputMode="numeric" autoComplete="postal-code" placeholder="00000-000"
          inputRef={cepInputRef} onChange={handleCepChanged}
          loading={draft.lookupCep !== null}
          loadingText="Buscando endereço pelo CEP…"
        />
        <AddressField
          label="Logradouro" field="logradouro" value={draft.values.logradouro}
          error={draft.errors.logradouro} autoComplete="address-line1"
          onChange={handleFieldChanged}
        />
        <AddressField
          label="Número" field="numero" value={draft.values.numero}
          error={draft.errors.numero} onChange={handleFieldChanged}
        />
        <AddressField
          label="Complemento (opcional)" field="complemento" value={draft.values.complemento}
          error={draft.errors.complemento} autoComplete="address-line2"
          onChange={handleFieldChanged}
        />
        <AddressField
          label="Bairro" field="bairro" value={draft.values.bairro}
          error={draft.errors.bairro} onChange={handleFieldChanged}
        />
        <AddressField
          label="Cidade" field="cidade" value={draft.values.cidade}
          error={draft.errors.cidade} autoComplete="address-level2"
          onChange={handleFieldChanged}
        />
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="billing-uf">Estado</Label>
          <Select value={draft.values.uf || undefined} onValueChange={handleUfChanged}>
            <SelectTrigger
              id="billing-uf"
              className="h-12 cursor-pointer text-base"
              aria-invalid={Boolean(draft.errors.uf)}
              aria-describedby={draft.errors.uf ? 'billing-uf-error' : undefined}
            >
              <SelectValue placeholder="UF" />
            </SelectTrigger>
            <SelectContent>
              {UF_OPTIONS.map((uf) => (
                <SelectItem key={uf} value={uf} className="cursor-pointer">{uf}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {draft.errors.uf && <p id="billing-uf-error" className="text-sm text-error-text" role="alert">{draft.errors.uf}</p>}
        </div>
      </div>

      <div className="flex gap-3">
        <Button type="submit" disabled={saving || draft.saving} className="cursor-pointer">
          {saving || draft.saving ? 'Salvando' : 'Salvar endereço'}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel} className="cursor-pointer">
            Cancelar
          </Button>
        )}
      </div>
    </form>
  );
}

function AddressField({
  label, field, value, error, onChange, inputRef,
  inputMode, autoComplete, placeholder, loading = false, loadingText,
}: {
  label: string;
  field: BillingAddressField;
  value: string;
  error?: string;
  onChange: (field: BillingAddressField, value: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  inputMode?: 'numeric';
  autoComplete?: string;
  placeholder?: string;
  loading?: boolean;
  loadingText?: string;
}) {
  const id = `billing-${field}`;
  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    onChange(field, event.target.value);
  }
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          ref={inputRef}
          value={value}
          onChange={handleChange}
          inputMode={inputMode}
          autoComplete={autoComplete}
          placeholder={placeholder}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          aria-busy={loading}
          // 16px+ on iOS or Safari zooms the page into the field.
          className="h-12 pr-10 text-base"
        />
        {loading && (
          <CircleNotchIcon
            className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin text-text-tertiary"
            aria-hidden
          />
        )}
      </div>
      {loading && loadingText && (
        <p className="text-sm text-text-secondary" role="status">{loadingText}</p>
      )}
      {error && <p id={`${id}-error`} className="text-sm text-error-text" role="alert">{error}</p>}
    </div>
  );
}
